import {type Json} from '@packages/utilities/json';
import {
  type ExpressionF,
  type ElementF,
  type ExtendF,
} from './GenericExpressionTypes';
import {
  type Qualified,
  type TypeName,
  type Identifier,
} from '@packages/systems/wfdl-Qualified';
import {type Type} from '../DataTypeDefinition';
import * as Variable from '../Variable';

type SelectF<a> = {
  from: a;
  prop: string;
};

type UnionF<a> = {
  tag: string;
  value: a;
};

type CaseF<a> = {
  value: a;
  cases: {
    [key: string]: a;
  };
};

type CallF<a> = {
  fun: a;
  arg: a;
};

type FunctionF<a> = {
  param: Identifier;
  body: a;
};

type TypedF<a> = {
  type: Type;
  expr: a;
};

export type ExpressionMatcher<r, o> = {
  Boolean: (arg1: boolean, arg2: ExpressionF<any>) => o;
  Number: (arg1: number, arg2: ExpressionF<any>) => o;
  Text: (arg1: string, arg2: ExpressionF<any>) => o;
  Enum: (arg1: string, arg2: ExpressionF<any>) => o;
  Record: (
    arg1: {
      [key: string]: r;
    },
    arg2: ExpressionF<r>
  ) => o;
  Select: (arg1: SelectF<r>, arg2: ExpressionF<r>) => o;
  Union: (arg1: UnionF<r>, arg2: ExpressionF<r>) => o;
  Case: (arg1: CaseF<r>, arg2: ExpressionF<r>) => o;
  List: (arg1: Array<r>, arg2: ExpressionF<r>) => o;
  Element: (arg1: ElementF<r>, arg2: ExpressionF<r>) => o;
  Variable: (arg1: Variable.VariableType, arg2: ExpressionF<any>) => o;
  Call: (arg1: CallF<r>, arg2: ExpressionF<r>) => o;
  Function: (arg1: FunctionF<r>, arg2: ExpressionF<r>) => o;
  Literal: (
    arg1: {
      name: Qualified<TypeName>;
      value: Json;
    },
    arg2: ExpressionF<any>
  ) => o;
  Hole: (arg1: Record<string, never>, arg2: ExpressionF<any>) => o;
  Extend: (arg1: ExtendF<r>, arg2: ExpressionF<r>) => o;
  Typed: (arg1: TypedF<r>, arg2: ExpressionF<r>) => o;
  Distinct: (
    arg1: {
      name: Qualified<TypeName>;
      value: r;
    },
    arg2: ExpressionF<r>
  ) => o;
};

export type PartialExpressionMatcher<r, o> = Partial<
  ExpressionMatcher<r, o>
> & {
  _: (arg1: ExpressionF<r>) => o;
};

export function matchExpression<r, o>(
  matchers: ExpressionMatcher<r, o> | PartialExpressionMatcher<r, o>
) {
  return function getMatcherFunction(expr: ExpressionF<r>): o {
    // @ts-expect-error its a thing
    const matcher = matchers[expr.type];

    // @ts-expect-error its a thing
    if (matcher) return matcher(expr.val, expr);

    // @ts-expect-error its a thing
    const fallback = matchers._;
    if (!fallback) {
      console.log(expr);
      throw new TypeError(
        // @ts-expect-error its a thing
        `matchExpression received an expression of type "${expr.type}" but no corresponding matcher function (or a default _) was provided.`
      );
    }
    return fallback(expr);
  };
}
